import { Box, Modal, Typography } from '@mui/material';

import { Activity } from '@/types';
import { getSpeed } from '@/utils/activities';
import { getSufferScoreColor } from '@/utils/common';
import { getCityName } from '@/utils/geography';
import { roundNumber } from '@/utils/maths';
import { useQuery } from '@tanstack/react-query';
import PathDisplay from './PathDisplay';
import styles from './activityModal.module.css';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

type Props = {
  open: boolean;
  handleClose: () => void;
  handleOpen: () => void;
  polyline: string;
  activity: Activity;
  chrono: string;
  date: string;
};

const ActivityModal = ({
  open,
  handleClose,
  polyline,
  activity,
  chrono,
  date,
}: Props) => {
  const {
    name,
    id,
    distance,
    average_speed,
    total_elevation_gain,
    average_watts,
    suffer_score,
    sport_type,
    calories,
    average_cadence,
    average_heartrate,
    average_temp,
    location_city,
    start_latlng,
    weighted_average_watts,
  } = activity;

  const { data, isLoading } = useQuery({
    queryKey: ['cityName', { id }],
    queryFn: () => getCityName(start_latlng[0], start_latlng[1]),
  });

  const cityNameFn = () => {
    if (isLoading) {
      return 'Chargement...';
    }

    if (data && data.address) {
      return `${data.address.city} - ${data.address.country}`;
    }
    return undefined;
  };

  const cityName = cityNameFn();

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        <Typography variant="h4" className={styles.title}>
          <a href={`https://www.strava.com/activities/${id}`}>{name}</a>
        </Typography>
        {polyline.length > 0 && (
          <>
            <Typography variant="h6" sx={{ borderBottom: '1px solid grey' }}>
              Tracé
            </Typography>
            <PathDisplay polyline={polyline} color="blue" />
          </>
        )}
        <Typography
          variant="h6"
          sx={{ borderBottom: '1px solid grey', marginBottom: 1 }}
        >
          Informations
        </Typography>
        <ul className={styles.list}>
          {location_city && (
            <li>
              <b>Lieu: </b>
              {location_city}
            </li>
          )}
          <li>
            <b>Date: </b>
            {date}
          </li>
          {cityName && (
            <li>
              <b>Lieu: </b>
              {cityName}
            </li>
          )}
          <li>
            <b>Distance: </b>
            {`${roundNumber(distance / 1000)} km`}
          </li>
          <li>
            <b>Chrono: </b>
            {chrono}
          </li>
          {['Ride', 'VirtualRide'].includes(sport_type) && (
            <li>
              <b>Vitesse: </b>
              {`${getSpeed('Ride', average_speed)} km/h`}
            </li>
          )}
          {sport_type === 'Run' && (
            <li>
              <b>Allure: </b>
              {`${getSpeed('Run', average_speed)} min/km`}
            </li>
          )}
          {sport_type === 'Swim' && (
            <li>
              <b>Allure: </b>
              {`${getSpeed('Swim', average_speed)} min/100m`}
            </li>
          )}
          {total_elevation_gain > 0 && (
            <li>
              <b>Dénivelé: </b>
              {`${roundNumber(total_elevation_gain)} m`}
            </li>
          )}
          {average_watts && (
            <li>
              <b>Puissance: </b>
              {`${roundNumber(average_watts)} W`}
            </li>
          )}
          {weighted_average_watts && (
            <li>
              <b>Puissance normalisée: </b>
              {`${roundNumber(weighted_average_watts)} W`}
            </li>
          )}
          {suffer_score && (
            <li>
              <b>Effort: </b>
              <span style={{ color: getSufferScoreColor(suffer_score) }}>
                {suffer_score}
              </span>
            </li>
          )}
          {calories && (
            <li>
              <b>Calories: </b>
              {calories} kcal
            </li>
          )}
          {average_heartrate && (
            <li>
              <b>Fréquence cardiaque: </b>
              {average_heartrate} bpm
            </li>
          )}
          {average_cadence && (
            <li>
              <b>Cadence: </b>
              {average_cadence} rpm
            </li>
          )}
          {average_temp && (
            <li>
              <b>Température: </b>
              {average_temp} °C
            </li>
          )}
        </ul>
      </Box>
    </Modal>
  );
};

export default ActivityModal;
